import * as React from 'react';
import cn from 'classnames';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { observer } from 'mobx-react';

import { ManagerSegmentStep } from 'shared/entities/managerSegment';
import { useTypedTranslation } from 'shared/entities/localization';
import ContentModal from 'shared/newComponents/modals/ContentModal';
import { ModalSize } from 'shared/newEntities/components/modals';
import { useUiStore } from 'stores/index';
import { useManagerSegmentStore } from 'stores/managerSegmentStore';

import QuestionStep from './QuestionStep';
import SegmentStep from './SegmentStep';
import SuccessStep from './SuccessStep';
import { createStepChunkVariants, createStepVariants } from './animation';
import './ManagerSegmentModal.modules.scss';

const SUCCESS_TITLE_ID = 'manager-segment-success-title';

const ManagerSegmentModalContent: React.FC = () => {
  const store = useManagerSegmentStore();
  const uiStore = useUiStore();
  const { t } = useTypedTranslation('app');
  const reducedMotion = useReducedMotion();
  const titleRef = React.useRef<HTMLHeadingElement>(null);
  const stepVariants = React.useMemo(
    () => createStepVariants(Boolean(reducedMotion)),
    [reducedMotion]
  );
  const chunkVariants = React.useMemo(
    () => createStepChunkVariants(Boolean(reducedMotion)),
    [reducedMotion]
  );
  const isQuestionStep = store.step === ManagerSegmentStep.questions;
  const isSuccess = store.step === ManagerSegmentStep.success;
  const currentQuestion = store.currentQuestion;

  React.useEffect(() => {
    titleRef.current?.focus();
  }, [store.step, store.questionIndex]);

  return (
    <ContentModal
      opened={store.modalState.opened}
      onClose={store.close}
      withHeader={!isSuccess}
      title={
        isQuestionStep && currentQuestion
          ? t(`managerSegment.questions.${currentQuestion.question}.title`)
          : t('managerSegment.segment.title')
      }
      subTitle={
        isQuestionStep
          ? t('managerSegment.questionProgress', {
              current: store.questionIndex + 1,
              total: store.totalQuestions
            })
          : t('managerSegment.step', { current: 1, total: 2 })
      }
      titleRef={titleRef}
      initialFocusRef={titleRef}
      ariaLabelledBy={isSuccess ? SUCCESS_TITLE_ID : undefined}
      fixHeight={uiStore.isMobile}
      fullScreen={uiStore.isMobile}
      fullScreenMobile={uiStore.isMobile}
      size={isSuccess ? ModalSize.s : ModalSize.m}
      styleName="manager-segment-modal"
    >
      <AnimatePresence initial={false} mode="wait">
        <motion.div
          key={`${store.step}-${store.questionIndex}`}
          variants={stepVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          styleName={cn(
            'manager-segment-modal__step',
            isQuestionStep && 'manager-segment-modal__step_questions',
            isSuccess && 'manager-segment-modal__step_success'
          )}
        >
          {store.step === ManagerSegmentStep.segment && (
            <SegmentStep
              chunkVariants={chunkVariants}
              onContinue={store.goToQuestions}
            />
          )}
          {isQuestionStep && (
            <QuestionStep
              chunkVariants={chunkVariants}
              onNext={store.goToNextQuestion}
              onBack={store.goToPreviousQuestion}
            />
          )}
          {isSuccess && (
            <SuccessStep
              titleId={SUCCESS_TITLE_ID}
              titleRef={titleRef}
              onAcknowledge={store.acknowledgeSuccess}
            />
          )}
        </motion.div>
      </AnimatePresence>
    </ContentModal>
  );
};

export default observer(ManagerSegmentModalContent);
